import { compact, first } from '@quromedical/utils';
import type { HumanName, Patient, Practitioner, RelatedPerson } from 'fhir/r4';

import type { HumanNamePrefix } from '../coding';

export type AnyPerson = Patient | Practitioner | RelatedPerson;
export type Gender = AnyPerson['gender'];

export const getNameText = (givenName = '', familyName = '') =>
  `${givenName} ${familyName}`.trim();

export const getPersonName = (person: AnyPerson) => {
  const givenName = first(person.name)?.given?.join(' ') || '';
  const familyName = first(person.name)?.family || '';

  return getNameText(givenName, familyName);
};

/**
 * Creates a `HumanName[]` from a given name
 * @deprecated please use createFullHumanName
 * @param givenName
 * @param familyName
 * @param use defaults to `Official`
 * @returns if the names are empty then will return `undefined`, otherwise it will return a
 * `HumanName[]` where the first and only element is the created name
 */
export const createHumanName = (
  givenName?: string,
  familyName?: string,
  use: HumanName['use'] = 'official',
): HumanName[] | undefined => {
  const given = givenName?.trim().length ? [givenName] : undefined;

  const family = familyName?.trim();

  const hasName = given || family;

  if (!hasName) {
    return undefined;
  }

  // using `text` so that we have something complete for using `display` in refs
  const text = getNameText(givenName, familyName);

  return [
    {
      use,
      given,
      family,
      text,
    },
  ];
};

interface HumanNameInput {
  given?: string;
  family?: string;
  prefix?: HumanNamePrefix;
  use?: HumanName['use'];
}

export const createFullHumanName = ({
  use = 'official',
  family,
  given,
  prefix,
}: HumanNameInput): HumanName[] | undefined => {
  const givenTrimmed = given?.trim();

  const familyTrimmed = family?.trim();

  const hasName = givenTrimmed || familyTrimmed;

  if (!hasName) {
    return undefined;
  }

  const text = getNameText(givenTrimmed, familyTrimmed);

  return [
    {
      use,
      text,
      family: familyTrimmed,
      given: givenTrimmed ? [givenTrimmed] : undefined,
      prefix: prefix ? [prefix] : undefined,
    },
  ];
};

interface Names {
  familyName?: string;
  givenName?: string;
  prefix?: HumanNamePrefix;
}

const getNamesFromText = (text?: string): Names => {
  if (!text) {
    return {};
  }

  const [givenName, ...rest] = text.split(' ');

  const familyName = rest.join(' ');

  return {
    givenName,
    familyName,
  };
};

/**
 * Gets the `familyName` and `givenNames` properties from a `Patient` or `Practitioner`
 * @param patient
 * @returns
 */
export const getPersonNames = (person: AnyPerson): Names => {
  const names = person.name;

  const fallbackName = first(names);

  if (!fallbackName) {
    return {};
  }

  const officialNames = person.name?.filter((n) => n.use === 'official');

  const name = first(officialNames) || fallbackName;

  const prefix = first(name.prefix) as HumanNamePrefix | undefined;

  const given = name.given?.join(' ');
  const { family } = name;

  const textNames = getNamesFromText(name.text);

  return {
    prefix,
    givenName: given || textNames.givenName,
    familyName: family || textNames.familyName,
  };
};

/**
 * Get the person name in the format of `first last`
 * @param person
 * @returns
 */
export const getDisplayName = (person: AnyPerson): string => {
  const name = getPersonNames(person);

  return compact([name.givenName, name.familyName]).join(' ');
};

export const getDisplayNameWithPrefix = (person: AnyPerson) => {
  const { familyName, givenName, prefix } = getPersonNames(person);
  return compact([prefix, givenName, familyName]).join(' ');
};
